import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ProductsWithoutStockApiAdapter } from '../adapters/ProductsWithoutStockApiAdapter';
import { Filters } from '../models/ProductsWithoutStock';
import { Service } from '../services/Service';

export const useProductsWithoutStock = (filters: Filters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ProductsWithoutStockApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['productsWithoutStock', filters],
    queryFn: ({ pageParam = 1 }) =>
      service.getPaginated({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  // 🎯 Procesar datos para incluir cálculos de la tabla
  const productsWithoutStock =
    data?.pages.flatMap((page) =>
      page.productsWithoutStock.map((product: any) => ({
        ...product,
        // Mapear campos de la nueva API a la estructura esperada
        requerido: product.total_quantity,
        disponible: product.available_stock,
        deficit: product.stock_deficit,
        pedido: product.orders.length, // Cantidad real de pedidos
        // Mapear campos del producto para compatibilidad
        id: product.product_id,
        name: product.product_name,
        code: product.product_code,
        image_hash: product.product_image_url,
      }))
    ) ?? [];

  // 🎯 Obtener summary de la primera página
  const summary = data?.pages?.[0]?.summary;

  // Obtener el total del API (siempre sera el mismo en todas las paginas)
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    productsWithoutStock,
    summary,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    // Nuevas props para scroll infinito
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    // Stats útiles
    totalPages: data?.pages.length ?? 0,
    totalProducts: productsWithoutStock.length,
    total: totalFromApi, // Total real del API
  };
};
